import * as React from "react";
import polarLogo from "../../../assets/images/polar.svg";
import azureLogo from "../../../assets/images/Azure.jpg";
import axios from "axios";
import { environment } from "src/environments/environment";

type LoginForm = {
  username: string;
  password: string;
};

export const useNavigate = () => {
  return (path: string) => {
    window.location.href = path;
  };
};

const PolarLogin = () => {
  const [form, setForm] = React.useState<LoginForm>({
    username: "",
    password: "",
  });
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginError, setLoginError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      signInWithAzure(token);
    }

    const loginError = sessionStorage.getItem('loginError');
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const togglePassword = () => setShowPassword(!showPassword);

  const signIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (!form.username || !form.password) {
      return;
    }

    const authPayload = new URLSearchParams();
    authPayload.append("client_id", "polar-client");
    authPayload.append("client_secret", "polar-secret");
    authPayload.append("grant_type", "password");
    authPayload.append("username", form.username);
    authPayload.append("password", form.password);

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${environment.host}/oauth/token`,
        authPayload.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Accept: "application/json, text/plain, */*",
          },
        }
      );

      const loginResponse = response.data;
      localStorage.setItem("USERNAME",form.username);
      localStorage.setItem("ACCESS_TOKEN", loginResponse.access_token);
      localStorage.setItem("ACCOUNT", loginResponse.references[0]);
      localStorage.setItem("REFERENCE", loginResponse.references[0]);
      localStorage.setItem("REFRESH_TOKEN", loginResponse.refresh_token);
      localStorage.setItem("ROLE", loginResponse.role);
      localStorage.setItem("USER_ID", loginResponse.id);

      if (loginResponse.role < 10000) {
        const reqUrl = localStorage.getItem("REQUESTED_URL");
        navigate(reqUrl ? reqUrl : "/projects");
        localStorage.setItem("REQUESTED_URL", "");
      } else if (loginResponse.role === 10000) {
        navigate("/kits");
      }

      setLoginError("");
    } catch (err) {
      setLoginError(
        (err.response && err.response.data && err.response.data.error_description) ||
          "Login failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithAzure = (token) => {
    console.log('Signing in with Azure...', token);
    const { clientId, tenantId } = environment.azureAd;
    const redirectUri = `${window.location.origin}/callback`;
    let state = "12345";
    if (typeof token == 'string') {
      state = `${state}|${token}`;
    }
    console.log('token', token);
    const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize` +
      `?client_id=${clientId}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_mode=query` +
      `&scope=openid profile email User.Read` +
      `&state=${state}`;
    window.location.href = authUrl;
  };

  // Style object for consistent styling
  const styles = {
    container: {
      fontFamily: "'Poppins', sans-serif",
    },
    form: {
      fontFamily: "'Poppins', sans-serif",
    },
    input: {
      fontFamily: "'Poppins', sans-serif",
    },
    button: {
      fontFamily: "'Poppins', sans-serif",
    },
    error: {
      fontFamily: "'Poppins', sans-serif",
    },
    link: {
      fontFamily: "'Poppins', sans-serif",
    }
  };

  return (
    <section id="login" style={styles.container}>
      <div className="login-container" style={styles.container}>
        <div className="login-form-div" style={styles.container}>
          <form onSubmit={signIn} className="form" style={styles.form}>
            <div className="form-input-block" style={styles.container}>
              <div className="logo-div">
                <img className="polar-logo" src={polarLogo} alt="Polar Logo" />
              </div>
              <label className="form-label" style={styles.container}>Username</label>
              <input
                className="form-input"
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="Enter username"
                style={styles.input}
              />
              {isSubmitted && !form.username && (
                <span className="form-error" style={styles.error}>Username is required</span>
              )}
            </div>

            <div className="form-input-block" style={styles.container}>
              <label className="form-label" style={styles.container}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="form-input"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Password"
                  style={{ paddingRight: "40px", width: "100%", ...styles.input }}
                />
                <button
                  type="button"
                  onClick={togglePassword}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#324054"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#324054"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
              {isSubmitted && !form.password && (
                <span className="form-error" style={styles.error}>Password is required</span>
              )}
            </div>

            {loginError && (
              <div className="custom-form-error" style={styles.error}>{loginError}</div>
            )}
            {isLoading && <div className="loading-text" style={styles.container}></div>}

            <div className="form-input-block" style={styles.container}>
              <button
                type="submit"
                className="btn login-btn"
                disabled={isLoading}
                style={styles.button}
              >
                {isLoading ? "Logging in..." : "Login"}
              </button>
              <div>
                <a href="/forgot-password" className="forget-link" style={styles.link}>
                  Forgot Password?
                </a>
              </div>
            </div>

            <div className="form-input-block" style={styles.container}>
              <button
                type="button"
                className="btn azure-btn"
                onClick={() => signInWithAzure(null)}
                style={styles.button}
              >
                <img src={azureLogo} alt="Azure" className="azure-logo" />
                Sign in with Azure
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PolarLogin;